import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

import { AuthSessionService } from './auth-session.service';

/**
 * Общий auth interceptor приложения.
 * Добавляет bearer-токен, делает ровно одну refresh-попытку на `401`
 * и не вмешивается в `403`, чтобы RBAC-ошибки не выглядели как logout.
 */
export const authInterceptor: HttpInterceptorFn =
  (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
    const session = inject(AuthSessionService);
    const router = inject(Router);

    const redirectToLogin = () => {
      void router.navigateByUrl('/auth/login', { replaceUrl: true });
    };

    // Специальные запросы пропускаем без bearer и без refresh-логики.
    if (req.headers.has('X-Skip-Auth')) {
      const clean = req.clone({ headers: req.headers.delete('X-Skip-Auth') });
      return next(clean);
    }

    const skipRefresh = req.headers.has('X-Skip-Refresh');
    const baseReq = skipRefresh
      ? req.clone({ headers: req.headers.delete('X-Skip-Refresh') })
      : req;

    // Подкладываем актуальный access token, если он есть.
    const at = session.getAccessToken();
    const requestSessionId = session.getSessionId();
    const authedReq = at
      ? baseReq.clone({ setHeaders: { Authorization: `Bearer ${at}` } })
      : baseReq;

    return next(authedReq).pipe(
      catchError((err: unknown) => {
        if (!(err instanceof HttpErrorResponse)) {
          return throwError(() => err);
        }

        // Только `401` считается поводом для восстановления токена. `403` — это обычный запрет доступа.
        if (err.status !== 401) {
          return throwError(() => err);
        }

        if (skipRefresh) {
          return throwError(() => err);
        }

        // Запрос старой учётной записи нельзя восстанавливать уже в новой сессии.
        if (!requestSessionId || session.getSessionId() !== requestSessionId) {
          return throwError(() => err);
        }

        // Делаем один общий refresh и один retry исходного запроса.
        return from(session.refreshRotate(true)).pipe(
          switchMap((ok) => {
            if (!ok) {
              if (session.getStatus() === 'guest') {
                // `refreshRotate(true)` уже выполнил `hardLogout()`, остаётся только вернуть пользователя на login.
                redirectToLogin();
              }
              return throwError(() => err);
            }

            if (session.getSessionId() !== requestSessionId) {
              return throwError(() => err);
            }

            const at2 = session.getAccessToken();
            const retryReq = at2
              ? baseReq.clone({ setHeaders: { Authorization: `Bearer ${at2}` } })
              : baseReq;

            return next(retryReq).pipe(
              catchError((retryErr: unknown) => {
                if (!(retryErr instanceof HttpErrorResponse) || retryErr.status !== 401) {
                  return throwError(() => retryErr);
                }

                if (session.getSessionId() !== requestSessionId) {
                  return throwError(() => retryErr);
                }

                return from(session.hardLogout(requestSessionId)).pipe(
                  switchMap(() => {
                    redirectToLogin();
                    return throwError(() => retryErr);
                  }),
                );
              }),
            );
          })
        );
      })
    );
  };
